import { useTablePropsInfo } from '@op/hooks/common';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import useRefresh from '@op/hooks/useRefresh';
import { DBO_POINT } from '@op/operationLog/buriedPoint';
import { downloadAction } from '@op/pages/download/utils';
import { getRealtimeSteamingTable } from '@op/services';
import { DBO_TM } from '@op/utils/format';
import { useUpdateEffect } from 'ahooks';
import { useEffect, useMemo, useRef, useState } from 'react';

export default function useSteamingTable({
  filter,
  firstNoDataCallBack,
}: {
  filter: any;
  firstNoDataCallBack: () => void;
}) {
  const {
    data,
    setData,
    total,
    setTotal,
    pageInfo,
    changePageInfo,
    sortBy,
    setSortBy,
    sortFilter,
  } = useTablePropsInfo({
    defaultSortBy: { by: 'rank', order: 'desc' },
    defaultPageInfo: { page: 1, page_size: 10 },
  });

  const [rankTimeList, setRankTimeList] = useState<{
    newTime: string[];
    oldTime: string[];
  }>({
    newTime: [],
    oldTime: [],
  });

  const { loading, wrapperCancelRequest } = useCancelRequestHook({
    clear: () => setData([]),
  });
  const params = useMemo(() => {
    const { by, order } = sortBy;
    return {
      ...(filter || {}),
      sort_item: by === 'rank' ? 'live_viewer' : by,
      sort_model: order,
      ...pageInfo,
    };
  }, [sortFilter, pageInfo]);

  const isFirst = useRef(true);

  function requestTable(loadingFlag?: boolean) {
    return wrapperCancelRequest(getRealtimeSteamingTable, params, loadingFlag, {
      user_cache_expire: 5,
    })
      .then(data => {
        const {
          list,
          total = 0,
          previous_ranking_datetime,
          current_ranking_datetime,
        } = data || {};

        setData(list || []);
        setTotal(total || 0);
        setRankTimeList({
          newTime: current_ranking_datetime?.start_time
            ? [
                current_ranking_datetime?.start_time,
                current_ranking_datetime?.end_time,
              ]
            : [],
          oldTime: previous_ranking_datetime?.start_time
            ? [
                previous_ranking_datetime?.start_time,
                previous_ranking_datetime?.end_time,
              ]
            : [],
        });

        if (total === 0 && isFirst.current) {
          firstNoDataCallBack();
        }
      })
      .catch(() => {})
      .finally(() => {
        isFirst.current = false;
      });
  }

  const {
    initTimer,
    date: time,
    addRefreshTask,
  } = useRefresh({
    key: 'real_time_streaming_refresh_time',
  });

  useEffect(() => {
    if (params?.edition_unified_id) {
      initTimer();
      requestTable();
      addRefreshTask('streamingTable', () => requestTable(true));
    }
  }, [params]);

  function downloadTable() {
    downloadAction({
      key: 'RealtimeStreamingTable',
      filter: { ...params, should_include_timeline: false },
      fileName: `Realtime Streaming Localtime${DBO_TM.utc().format(
        'YYYY-MM-DD HH:mm:ss',
      )}`,
      needFilterTime: false,
    });
  }

  useUpdateEffect(() => {
    changePageInfo({ ...pageInfo, page: 1 });
  }, [filter]);

  function changeSortBy(sortItem: any) {
    setSortBy(sortItem);
    changePageInfo({ ...pageInfo, page: 1 });
    DBO_POINT.btn('SortBy', undefined, sortItem.by);
  }

  return {
    loading,
    data,
    total,
    sortBy,
    setSortBy: changeSortBy,
    pageInfo,
    changePageInfo,
    params,
    download: downloadTable,
    time,
    rankTimeList,
  };
}
